import { Injectable } from '@angular/core';
import { SQLite, SQLiteObject } from '@awesome-cordova-plugins/sqlite/ngx';
import { Mdc, MdcOffline, MdcOfflineSync } from '../mdc/mdc.model';

@Injectable({
  providedIn: 'root'
})
export class SqlliteDbService {

  private db!: SQLiteObject;
  private isService: boolean = false;
  constructor(private sqlite: SQLite) { }

  async initializeOpenConnection(): Promise<boolean> {
    try {
      this.db = await this.sqlite.create({ name: 'questionarios.db', location: 'default' });
      await this.createTables();
      this.isService = true;
      console.log('SQLite connection opened and tables created');
      return true;
    } catch (error) {
      console.error('Erro ao abrir conexão SQLite:', error);
      return false;
    }
  }

  private async createTables(): Promise<void> {
    const sql = `
      CREATE TABLE IF NOT EXISTS questionarios (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        email TEXT NOT NULL,
        created_at INTEGER DEFAULT (strftime('%s', 'now')),
        updated_at INTEGER DEFAULT (strftime('%s', 'now')),
        sync_status INTEGER DEFAULT 0,
        sync_attempts INTEGER DEFAULT 0,
        last_sync_error TEXT,
        json_data TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_questionarios_email ON questionarios (email);
      CREATE INDEX IF NOT EXISTS idx_questionarios_sync ON questionarios (sync_status, created_at);
    `;

    // Note que o Cordova SQLite não aceita múltiplas queries em um executeSql só, então precisa separar
    const statements = sql.split(';').map(s => s.trim()).filter(s => s.length);

    for (const stmt of statements) {
      await this.db.executeSql(stmt, []);
    }
  }

  async salvarQuestionario(mdc: Mdc, email: string): Promise<boolean> {
    if (!this.isService) return false;
    const jsonData = this.serializeMdc(mdc);
    try {
      await this.db.executeSql(
        `INSERT INTO questionarios (email, json_data) VALUES (?, ?)`,
        [email, jsonData]
      );
      return true;
    } catch (error) {
      console.error('Erro ao salvar questionário:', error);
      return false;
    }
  }

  async carregarQuestionariosOffline(): Promise<MdcOffline[]> {
    const questionarios = await this.getQuestionariosNaoSincronizados();

    return questionarios.map(item => {
      const offline = new MdcOffline();
      offline.id = item.id;
      offline.mdc = Object.assign(new Mdc(), item.mdc);
      offline.data = item.created_at;
      return offline;
    });
  }

  async getQuestionarioOfflineById(id: number): Promise<MdcOffline | null> {
    if (!this.isService) return null;

    try {
      const res = await this.db.executeSql(
        `SELECT id, email, json_data, created_at FROM questionarios WHERE id = ?`, [id]
      );

      if (res.rows.length > 0) {
        const item = res.rows.item(0);
        const offline = new MdcOffline();
        offline.id = item.id;
        offline.mdc = this.deserializeMdc(item.json_data);
        offline.data = new Date(item.created_at * 1000);
        return offline;
      }

      return null;
    } catch (error) {
      console.error('Erro ao obter questionário por ID:', error);
      return null;
    }
  }

  async removerQuestionarioOfflinePorId(id: number): Promise<boolean> {
    if (!this.isService) return false;

    try {
      await this.db.executeSql(
        `DELETE FROM questionarios WHERE id = ?`, [id]
      );
      return true;
    } catch (error) {
      console.error('Erro ao remover questionário por ID:', error);
      return false;
    }
  }

  async getQuestionariosNaoSincronizados(): Promise<{ id: number, mdc: Mdc, email: string, created_at: Date }[]> {
    if (!this.isService) return [];
    try {
      const res = await this.db.executeSql(
        `SELECT id, email, json_data, created_at FROM questionarios WHERE sync_status = 0 ORDER BY created_at ASC`, []
      );

      const results = [];
      for (let i = 0; i < res.rows.length; i++) {
        const item = res.rows.item(i);
        results.push({
          id: item.id,
          email: item.email,
          mdc: this.deserializeMdc(item.json_data),
          created_at: new Date(item.created_at * 1000)
        });
      }

      return results;
    } catch (error) {
      console.error('Erro ao obter questionários não sincronizados:', error);
      return [];
    }
  }

  async getQuestionariosPorEmail(email: string): Promise<{ id: number, mdc: Mdc }[]> {
    if (!this.isService) return [];
    try {
      const res = await this.db.executeSql(
        `SELECT id, json_data FROM questionarios WHERE email = ? ORDER BY created_at DESC`,
        [email]
      );

      const results = [];
      for (let i = 0; i < res.rows.length; i++) {
        const item = res.rows.item(i);
        results.push({
          id: item.id,
          mdc: this.deserializeMdc(item.json_data)
        });
      }
      return results;
    } catch (error) {
      console.error('Erro ao obter questionários por email:', error);
      return [];
    }
  }

  private serializeMdc(mdc: Mdc): string {
    const toSave: Record<string, any> = {};
    Object.keys(mdc).forEach(key => {
      const value = mdc[key];
      if (value instanceof Date) {
        toSave[key] = value.toISOString();
      } else {
        toSave[key] = value;
      }
    });
    return JSON.stringify(toSave);
  }

  private deserializeMdc(json: string): Mdc {
    const raw = JSON.parse(json);
    const mdc = new Mdc();
    Object.keys(raw).forEach(key => {
      if (key === 'mdc_4' && typeof raw[key] === 'string') {
        mdc[key] = new Date(raw[key]);
      } else if (Array.isArray(mdc[key]) && !Array.isArray(raw[key])) {
        mdc[key] = raw[key] ? [raw[key]] : [];
      } else {
        mdc[key] = raw[key];
      }
    });
    return mdc;
  }

  async marcarComoSincronizado(id: number): Promise<boolean> {
    if (!this.isService) return false;
    try {
      await this.db.executeSql(
        `UPDATE questionarios 
          SET sync_status = 1,
              updated_at = strftime('%s', 'now'),
              sync_attempts = 0,
              last_sync_error = NULL
          WHERE id = ?`,
        [id]
      );
      return true;
    } catch (error) {
      console.error('Erro ao marcar como sincronizado:', error);
      return false;
    }
  }

  async marcarErroSincronizacao(id: number, error: string): Promise<boolean> {
    if (!this.isService) return false;
    try {
      await this.db.executeSql(
        `UPDATE questionarios 
          SET sync_status = 2,
              sync_attempts = sync_attempts + 1,
              last_sync_error = ?,
              updated_at = strftime('%s', 'now')
          WHERE id = ?`,
        [error.substring(0, 255), id]
      );
      return true;
    } catch (error) {
      console.error('Erro ao salvar resposta offline:', error);
      return false;
    }
  }

  async getTotalPendentes(): Promise<number> {
    if (!this.isService) return 0;
    try {
      const res = await this.db.executeSql(
        `SELECT COUNT(*) as total FROM questionarios WHERE sync_status = 0`, []
      );
      if (res.rows.length > 0) {
        return res.rows.item(0).total;
      }
      return 0;
    } catch (error) {
      console.error('Erro ao trazer total pendentes:', error);
      return 0;
    }
  }

  async limparDadosSincronizados(): Promise<boolean> {
    if (!this.isService) return false;
    try {
      await this.db.executeSql(`DELETE FROM questionarios WHERE sync_status = 1`, []);
      return true;
    } catch (error) {
      console.error('Erro ao limpar dados sincronizados:', error);
      return false;
    }
  }

  //#region Para efeito de verificação
  async carregarTodosOsQuestionariosOffline(): Promise<MdcOfflineSync[]> {
    if (!this.isService) return [];

    try {
      const res = await this.db.executeSql(
        `SELECT id, email, created_at, sync_status, json_data FROM questionarios ORDER BY created_at`,
        []
      );

      const result: MdcOfflineSync[] = [];

      for (let i = 0; i < res.rows.length; i++) {
        const item = res.rows.item(i);

        result.push({
          id: item.id,
          email: item.email,
          created_at: new Date(item.created_at * 1000),
          sync_status: item.sync_status,
          mdc: JSON.parse(item.json_data)
        });
      }

      return result;
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      return [];
    }
  }
  //#endregion
}
